import { Component, ComponentRef, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterEvent } from '@angular/router';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';

import { TokenService } from '@services/token.service';
import { CompaniesService } from '@services/companies.service';
import { CompanyModel } from '@data-layer/models/companies';
import { UserModel } from '@data-layer/models/user';
import { AppLayoutService } from '../../app-layout.service';
import { IHeader, IHeaderItem } from '../../interfaces';
import { GoogleAnalyticsReportService } from '@services/google-analytics-report.service';
import { GenericService } from '@services/generic.service';
import { takeUntil, withLatestFrom } from 'rxjs/operators';
import { InternalNote } from '@shared/internal-notes/interfaces';
import { HeaderType } from '@app/modules/app-layout/enums/headerType';
import { InternalNotesComponent } from '@shared/internal-notes/components';
import { AppService } from '@app/app.service';
import { EntrySummaryRepository, IsfRepository } from '@data-layer/repositories';
import { enumUserBetaFlag } from '@data-layer/enums/user';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  public headerData: IHeader = null;
  public user: UserModel;
  public internalNote: InternalNote;
  public headerType: HeaderType;
  public isAccountSettings: boolean = false;
  public isPayment: boolean = false;

  public isMobile: boolean;
  public isGreenStyle: boolean;
  public isPurpleHeader: boolean;

  public enumUserBetaFlag: typeof enumUserBetaFlag = enumUserBetaFlag;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    public router: Router,
    private companiesService: CompaniesService,
    private appLayoutService: AppLayoutService,
    private tokenService: TokenService,
    private titleService: Title,
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
    private googleAnalyticsReportService: GoogleAnalyticsReportService,
    private genericService: GenericService,
    private appService: AppService,
    private entrySummaryRepository: EntrySummaryRepository,
    private isfRepository: IsfRepository,
  ) {}

  public ngOnInit(): void {
    this.user = this.companiesService.getUser();

    this.appLayoutService.internalNotes$.pipe(takeUntil(this.destroy$)).subscribe((internalNote: InternalNote) => {
      this.internalNote = internalNote;
    });

    this.appLayoutService.headerTypeSource$.pipe(takeUntil(this.destroy$)).subscribe((headerType: HeaderType) => {
      this.headerType = headerType;
    });

    combineLatest([this.genericService.isMobile$, this.genericService.isLaptop$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([val1, val2]) => {
        this.isMobile = val1 || val2;
      });

    this.genericService.isGreenHeader$.pipe(takeUntil(this.destroy$)).subscribe((val: boolean) => {
      this.isGreenStyle = val;
    });
    this.genericService.isPurpleHeader$.pipe(takeUntil(this.destroy$)).subscribe((val: boolean) => {
      this.isPurpleHeader = val;
    });

    this.appLayoutService.contentHeader$.pipe(takeUntil(this.destroy$)).subscribe((val: IHeader) => {
      this.headerData = val;
      //TODO: code for understanding show/hide "back" icon
      // TODO: VERY VERY dirty solution. PM knows, and accepts... UI vs Bundle
      this.isAccountSettings = val?.title[0]?.key.includes('ACCOUNT_SETTINGS');
      this.isPayment = !!(val?.title || []).find((item: IHeaderItem) => item?.key.includes('COMPLETE_PAYMENT'));

      if (val) {
        const firstSegment = val.title && val.title.length ? val.title[0] : null;
        const tabSegment = val.tabTitle ? val.tabTitle : firstSegment;

        this.titleService.setTitle(
          [this.translateService.instant('APPLICATION_NAME'), this.translateService.instant(tabSegment.key, tabSegment.params)].join(' - '),
        );
      }
    });

    let isGreenHeaderBefore: boolean, isWhiteHeaderBefore: boolean; // #9028
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((val: RouterEvent) => {
      if (val instanceof NavigationEnd) {
        this.genericService.isGreenHeader = false;

        isGreenHeaderBefore = undefined;
        isWhiteHeaderBefore = undefined;
      }
    });

    // #9028
    this.appService.isOpen$
      .pipe(withLatestFrom(this.appService.isPurple$), takeUntil(this.destroy$))
      .subscribe(([isOpen, isPurpleSidebar]: [boolean, boolean]) => {
        if (isGreenHeaderBefore === undefined && isWhiteHeaderBefore === undefined) {
          isGreenHeaderBefore = this.isGreenStyle;
          isWhiteHeaderBefore = !this.isGreenStyle && !this.isPurpleHeader;
        }

        if (isOpen && isPurpleSidebar) {
          this.genericService.isGreenHeader = false;
          this.genericService.isPurpleHeader = true;
        }
        if (!isOpen) {
          if (isGreenHeaderBefore) {
            this.genericService.isGreenHeader = true;
            this.genericService.isPurpleHeader = false;
          }
          if (isWhiteHeaderBefore) {
            this.genericService.isGreenHeader = false;
            this.genericService.isPurpleHeader = false;
          }
        }
      });
  }

  public ngOnDestroy(): void {
    this.titleService.setTitle(this.translateService.instant('APPLICATION_NAME'));
    this.genericService.isGreenHeader = false;

    this.destroy$.next();
  }

  public openSettingsMenu(): void {
    this.appLayoutService.openSettingSidebar$.next();
  }

  public goBack(): void {
    this.router.navigateByUrl(this.activatedRoute.snapshot?.queryParams?.returnUrl || '/');
  }

  public openInternalNotes(): void {
    const componentSidebarRef: ComponentRef<InternalNotesComponent> = this.appService.openSidebar(InternalNotesComponent, {
      sidebarHeaderText: !!this.internalNote.internalNotes ? 'LABELS.INTERNAL_NOTES_EDIT' : 'LABELS.INTERNAL_NOTES_ADD',
      tooltipText: !!this.internalNote.internalNotes ? 'LABELS.INTERNAL_NOTES_EDIT_DESCRIPTION' : 'LABELS.INTERNAL_NOTES_ADD_DESCRIPTION',
    });
    componentSidebarRef.instance.internalNote = this.internalNote;
    componentSidebarRef.instance.saveInternalNotes.pipe(takeUntil(this.destroy$)).subscribe((data: InternalNote) => {
      (this.internalNote.isIsfPlacement ? this.updateIsfInternalNotes(data) : this.updateEsInternalNotes(data)).subscribe(() => {
        this.appLayoutService.internalNotes$.next({
          ...this.internalNote,
          internalNotes: data.internalNotes,
          internalReferenceNumber: data.internalReferenceNumber,
        });
        this.appLayoutService.internalNotesUpdated$.next();
        this.appService.closeSidebar();
      });
    });
  }

  public updateIsfInternalNotes(data: InternalNote): Observable<void> {
    return this.isfRepository.updateInternalNote(this.internalNote.id, data).pipe(takeUntil(this.destroy$));
  }

  public updateEsInternalNotes(data: InternalNote): Observable<void> {
    return this.entrySummaryRepository.updateInternalNote(this.internalNote.id, data).pipe(takeUntil(this.destroy$));
  }

  public get isShowInternalNotes(): boolean {
    return this.headerType === HeaderType.ESNotes || this.headerType === HeaderType.ISFNotes;
  }

  public get queryParams() {
    return this.activatedRoute && this.activatedRoute.snapshot ? this.activatedRoute.snapshot.queryParams : undefined;
  }

  public get company(): CompanyModel {
    return this.companiesService.getCompany();
  }

  public get companies(): CompanyModel[] {
    return this.companiesService.getCompanies() || [];
  }

  public goToCompany(): void {
    const companyTabLink: string =
      this.companies.length > 1
        ? '/companies/account-settings/company-list'
        : `/companies/account-settings/company-view/${this.company?.id}`;
    this.router.navigate([companyTabLink]);
  }

  public get mobileTitle(): IHeaderItem[] {
    return this.headerData ? this.headerData.mobileTitle || this.headerData.title || null : null;
  }

  public toggleSidebar() {
    this.appLayoutService.openSidebarMenu$.next();
  }

  public logout(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.tokenService.eraseToken(true, false);
    this.googleAnalyticsReportService.logout();
  }
}
